import React, { useRef, useState, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';

interface SignatureCanvasProps {
  onSignatureChange: (isEmpty: boolean) => void;
  onSignatureData: (dataUrl: string) => void;
  disabled?: boolean;
  initialData?: string; // Optional initial signature data URL to restore
}

const SignaturePad: React.FC<SignatureCanvasProps> = ({
  onSignatureChange,
  onSignatureData,
  disabled = false,
  initialData,
}) => {
  const sigPadRef = useRef<SignatureCanvas>(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  // Track if we're restoring to prevent unnecessary callbacks
  const isRestoringRef = useRef(false);
  const hasInitializedRef = useRef(false);
  const [canvasReady, setCanvasReady] = useState(false);
  
  // Track touch events to prevent duplicate mouse events on iPad/Android tablets
  const touchActiveRef = useRef(false);
  const touchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize and restore signature after component mounts
  useEffect(() => {
    // Wait for next tick to ensure canvas is fully mounted
    const timer = setTimeout(() => {
      if (sigPadRef.current && !hasInitializedRef.current) {
        try {
          const canvas = sigPadRef.current.getCanvas();
          if (canvas) {
            hasInitializedRef.current = true;
            setCanvasReady(true);
            
            // Restore initial signature data if provided
            if (initialData && initialData.trim() !== '') {
              try {
                isRestoringRef.current = true;
                // Don't clear first - fromDataURL will overwrite
                sigPadRef.current.fromDataURL(initialData);
                const isEmptyNow = sigPadRef.current.isEmpty();
                setIsEmpty(isEmptyNow);
                onSignatureChange(isEmptyNow);
                isRestoringRef.current = false;
              } catch (error) {
                console.error('Failed to restore signature:', error);
                isRestoringRef.current = false;
                setIsEmpty(true);
                onSignatureChange(true);
              }
            } else {
              setIsEmpty(true);
              onSignatureChange(true);
            }
          }
        } catch (error) {
          console.error('Canvas initialization error:', error);
          hasInitializedRef.current = false;
        }
      }
    }, 150);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prevent duplicate mouse events after touch events on iPad/Android tablets
  // This fix ensures touch events don't trigger duplicate mouse events
  useEffect(() => {
    if (!canvasReady || !sigPadRef.current) {
      return;
    }

    const canvas = sigPadRef.current.getCanvas();
    if (!canvas) {
      return;
    }

    // Handler for touch events - mark that touch is active
    const handleTouchStart = (e: TouchEvent) => {
      touchActiveRef.current = true;
      
      // Clear any existing timeout
      if (touchTimeoutRef.current) {
        clearTimeout(touchTimeoutRef.current);
      }
      
      // Reset the flag after a short delay (enough for mouse events to be suppressed)
      touchTimeoutRef.current = setTimeout(() => {
        touchActiveRef.current = false;
        touchTimeoutRef.current = null;
      }, 500);
    };

    // Handler to prevent mouse events that follow touch events
    const handleMouseDown = (e: MouseEvent) => {
      // Only prevent if touch was recently active (on touch devices)
      if (touchActiveRef.current) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      // Only prevent if touch was recently active (on touch devices)
      if (touchActiveRef.current) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      // Only prevent if touch was recently active (on touch devices)
      if (touchActiveRef.current) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    };

    // Add event listeners to the canvas
    canvas.addEventListener('touchstart', handleTouchStart, { passive: true });
    canvas.addEventListener('mousedown', handleMouseDown, true); // Use capture phase
    canvas.addEventListener('mousemove', handleMouseMove, true);
    canvas.addEventListener('mouseup', handleMouseUp, true);

    // Cleanup function
    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('mousedown', handleMouseDown, true);
      canvas.removeEventListener('mousemove', handleMouseMove, true);
      canvas.removeEventListener('mouseup', handleMouseUp, true);
      
      // Clear timeout if component unmounts
      if (touchTimeoutRef.current) {
        clearTimeout(touchTimeoutRef.current);
        touchTimeoutRef.current = null;
      }
      touchActiveRef.current = false;
    };
  }, [canvasReady]);

  // Handle initialData changes after component is mounted and initialized
  useEffect(() => {
    if (hasInitializedRef.current && sigPadRef.current && initialData !== undefined) {
      // Small delay to ensure previous operations are complete
      const timer = setTimeout(() => {
        if (sigPadRef.current && hasInitializedRef.current) {
          if (initialData && initialData.trim() !== '') {
            try {
              isRestoringRef.current = true;
              sigPadRef.current.fromDataURL(initialData);
              const isEmptyNow = sigPadRef.current.isEmpty();
              setIsEmpty(isEmptyNow);
              onSignatureChange(isEmptyNow);
              isRestoringRef.current = false;
            } catch (error) {
              console.error('Failed to restore signature:', error);
              isRestoringRef.current = false;
              setIsEmpty(true);
              onSignatureChange(true);
            }
          } else {
            if (!sigPadRef.current.isEmpty()) {
              sigPadRef.current.clear();
            }
            setIsEmpty(true);
            onSignatureChange(true);
          }
        }
      }, 50);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  const handleEnd = () => {
    if (sigPadRef.current && !isRestoringRef.current) {
      const isEmptyNow = sigPadRef.current.isEmpty();
      setIsEmpty(isEmptyNow);
      onSignatureChange(isEmptyNow);

      if (!isEmptyNow) {
        const dataUrl = sigPadRef.current.toDataURL('image/png');
        onSignatureData(dataUrl);
      }
    }
  };

  const handleClear = () => {
    if (sigPadRef.current) {
      sigPadRef.current.clear();
      setIsEmpty(true);
      onSignatureChange(true);
      onSignatureData('');
    }
  };

  // Use fixed pixel dimensions like the working example
  // Canvas needs actual pixel values, not percentages
  const canvasWidth = 800;
  const canvasHeight = isMobile ? 250 : isTablet ? 280 : 300;

  return (
    <Box>
      <Box
        sx={{
          border: '2px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.paper',
          position: 'relative',
          overflow: 'hidden',
          mb: 2,
          opacity: disabled ? 0.6 : 1,
          width: '100%',
          padding: 1.25, // ~10px padding like the working example
          pointerEvents: disabled ? 'none' : 'auto',
        }}
      >
        <Box
          sx={{
            width: '100%',
            pointerEvents: 'auto', // Ensure canvas area is always interactive when not disabled
          }}
        >
          <SignatureCanvas
            ref={sigPadRef}
            canvasProps={{
              width: canvasWidth,
              height: canvasHeight,
              style: {
                display: 'block',
                width: '100%',
                height: 'auto',
                touchAction: 'none',
                cursor: 'crosshair',
              },
            }}
            onEnd={handleEnd}
            backgroundColor="#ffffff"
            penColor="#000000"
          />
        </Box>
        {isEmpty && !disabled && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none',
              color: 'text.secondary',
              zIndex: 1,
            }}
          >
            <Typography variant="body2" sx={{ opacity: 0.5 }}>
              Sign here
            </Typography>
          </Box>
        )}
      </Box>
      <Button
        variant="outlined"
        size="small"
        startIcon={<ClearIcon />}
        onClick={handleClear}
        disabled={disabled || isEmpty}
        sx={{ mb: 1 }}
      >
        Clear Signature
      </Button>
    </Box>
  );
};

export default SignaturePad;

